/**
 * The three failures that carry data the screen has to show.
 *
 * Kept apart from `Surveys.service.ts` so the removable mock can raise them without importing the
 * service that imports it — and because "this error means open the version dialog" is a decision,
 * not a string.
 *
 * @module Pages/BUs/Surveys
 */

/**
 * The trigger names the screen shows, repeated here rather than imported.
 *
 * This module stays free of the format helpers on purpose — the mock raises these errors, and the
 * service imports the mock — and three strings are a cheaper price than that edge. A trigger with no
 * entry falls back to its enum value, so a new one never prints as `undefined`.
 */
const TRIGGER_TEXT: Record<string, string> = {
  AUTO: 'Abre sozinha',
  ON_DEMAND: 'Sob demanda',
  ON_OPT_OUT: 'Ao desativar',
};

/**
 * `409` from `PUT /:id/questions`: the version being edited already has answers.
 *
 * It carries `responseCount` because the version dialog has to say how many people each choice
 * affects — "só corrigir" leaves those people out of the queue, "perguntar de novo" puts them
 * back in. Without the number the dialog would be two buttons and no decision.
 */
export class SurveyVersionLockedError extends Error {
  readonly responseCount: number;

  constructor(message: string, responseCount: number) {
    super(message);
    this.name = 'SurveyVersionLockedError';
    this.responseCount = responseCount;
  }
}

/**
 * `409` from `PATCH /:id`: the survey already has answers, so its trigger can no longer change (R44).
 *
 * Kept apart from {@link SurveyVersionLockedError} even though both are a `409` carrying a
 * `responseCount`: that one means "this save needs a new version" and opens the version dialog, and
 * answering *this* refusal with that dialog would offer a bump that changes nothing — versioning
 * clones the questionnaire, and the questionnaire is not what changed. The fields `currentTrigger`
 * and `requestedTrigger` are what tell the two apart, so the parse reads them and never the prose.
 *
 * **The message is rewritten in Portuguese**, same reason as the exit-questionnaire conflict: the
 * BFF's own text is a paragraph of English written for an API log. It names both triggers — with the
 * labels the field itself shows, not the enum values — and both ways out, because "não pode" with no
 * destination is what makes somebody try the same swap again through another route.
 */
export class SurveyTriggerLockedError extends Error {
  readonly responseCount: number;
  readonly currentTrigger: string;
  readonly requestedTrigger: string;

  constructor(responseCount: number, currentTrigger: string, requestedTrigger: string) {
    const answers = responseCount === 1 ? '1 resposta' : `${responseCount} respostas`;
    const label = (trigger: string) => TRIGGER_TEXT[trigger] ?? trigger;

    super(
      `A pesquisa já tem ${answers}, então o gatilho não pode mais mudar de ` +
        `"${label(currentTrigger)}" para "${label(requestedTrigger)}". Ele decide quem é ` +
        'perguntado e quando, e trocá-lo faria as respostas já coletadas viverem sob uma pesquisa ' +
        'que pergunta a outro público, em outro momento, sob o mesmo slug. Crie uma pesquisa nova ' +
        'com o gatilho que você quer — ou, se estas respostas forem de teste, resete esta pesquisa ' +
        'antes. Nada foi alterado.',
    );
    this.name = 'SurveyTriggerLockedError';
    this.responseCount = responseCount;
    this.currentTrigger = currentTrigger;
    this.requestedTrigger = requestedTrigger;
  }
}

/**
 * Reads a `409` body and returns the trigger refusal, or `null`.
 *
 * Demands **both** triggers plus the count: the version lock sends a `responseCount` too, and
 * claiming this failure on that body would replace a dialog that resolves the save with a toast that
 * explains nothing. Returning `null` here simply lets the next branch have it.
 */
export const parseTriggerConflict = (body: {
  responseCount?: unknown;
  currentTrigger?: unknown;
  requestedTrigger?: unknown;
}): SurveyTriggerLockedError | null => {
  const { responseCount: count, currentTrigger: current, requestedTrigger: requested } = body;

  if (typeof count !== 'number') return null;
  if (typeof current !== 'string' || !current) return null;
  if (typeof requested !== 'string' || !requested) return null;

  return new SurveyTriggerLockedError(count, current, requested);
};

/** `422` from `POST /:id/approve`: which languages are missing, so the screen can name them. */
export class SurveyMissingTranslationsError extends Error {
  readonly missingLanguages: string[];

  constructor(message: string, missingLanguages: string[]) {
    super(message);
    this.name = 'SurveyMissingTranslationsError';
    this.missingLanguages = missingLanguages;
  }
}

/**
 * `409` on create: the slug is held by a survey that was **deleted**, not by a live one.
 *
 * It carries the deleted survey's id and date because the only useful answer to this conflict is
 * an offer to restore that row — the slug cannot be reused while it exists, and the listing hides
 * it by default. A live survey holding the slug raises a plain `Error` instead: there is nothing
 * to offer, and "já existe" is the whole truth.
 */
export class SurveySlugHeldByDeletedError extends Error {
  readonly deletedSurveyId: string;
  readonly deletedAt: string | null;

  constructor(message: string, deletedSurveyId: string, deletedAt: string | null) {
    super(message);
    this.name = 'SurveySlugHeldByDeletedError';
    this.deletedSurveyId = deletedSurveyId;
    this.deletedAt = deletedAt;
  }
}

/**
 * `409` on create, update or restore: the opt-in feature already has an exit questionnaire (R45).
 *
 * It carries the survey holding the slot — id, slug and `approvalStatus` — because "já existe um"
 * is only half an answer. The other half is *which one*, so the person can go and edit it instead
 * of trying the same write again under a different slug.
 *
 * **The message is rewritten in Portuguese here.** The BFF's own text is several lines of English
 * explaining why two would fail silently, which is the right thing for an API log and the wrong
 * thing for a toast. The fields are read from the body, not parsed out of prose, so this cannot
 * misfire on some other `409`.
 */
export class ExitSurveyAlreadyExistsError extends Error {
  readonly conflictingSurveyId: string;
  readonly conflictingSurveySlug: string;
  readonly conflictingSurveyApprovalStatus: string | null;

  constructor(
    conflictingSurveyId: string,
    conflictingSurveySlug: string,
    conflictingSurveyApprovalStatus: string | null,
  ) {
    super(
      `A funcionalidade opt-in já tem um questionário de saída: "${conflictingSurveySlug}". ` +
        'Só pode haver um por funcionalidade — dois nunca falhariam na cara: um seria entregue e ' +
        'o outro ficaria no ar sem coletar nada. Edite o que já existe, ou exclua-o antes de ' +
        'anexar outro. Nada foi alterado.',
    );
    this.name = 'ExitSurveyAlreadyExistsError';
    this.conflictingSurveyId = conflictingSurveyId;
    this.conflictingSurveySlug = conflictingSurveySlug;
    this.conflictingSurveyApprovalStatus = conflictingSurveyApprovalStatus;
  }
}

/**
 * Reads a `409` body and returns the exit-questionnaire conflict, or `null`.
 *
 * Pure, and separate from the service, for the same reason `parseDeletedHolder` is: deciding which
 * `409` this is has to be testable without a `Response`. Unlike that one it reads **fields**, not
 * prose — the BFF sends `conflictingSurveyId` and `conflictingSurveySlug` — so a message that
 * changes wording cannot break it, and another `409` cannot be mistaken for this one.
 */
export const parseExitSurveyConflict = (body: {
  conflictingSurveyId?: unknown;
  conflictingSurveySlug?: unknown;
  conflictingSurveyApprovalStatus?: unknown;
}): ExitSurveyAlreadyExistsError | null => {
  const { conflictingSurveyId: id, conflictingSurveySlug: slug } = body;

  if (typeof id !== 'string' || !id) return null;
  if (typeof slug !== 'string' || !slug) return null;

  const status = body.conflictingSurveyApprovalStatus;

  return new ExitSurveyAlreadyExistsError(id, slug, typeof status === 'string' ? status : null);
};
